// ── PETLING CASE STUDIES: CONTENT ──────────────────────────────────────────
// All copy for the four Petling pages lives in this file.
// Layout and styling live in PetlingSuite.tsx. Image URLs live in images.ts.

export type PageId = "app" | "admin" | "providers" | "website";
export type Tone = "light" | "white" | "dark";

export interface Theme {
  accent: string;        // accent on light backgrounds
  accentOnDark: string;  // accent on dark backgrounds
  pale: string;          // pale card background
  deep: string;          // deep band / dark card background
  heroBg: string;
  heroDark: boolean;
  glow: string;
}

export interface ImgSlot { id: string; caption: string; ratio?: string; }
export interface Point { icon: string; title: string; desc: string; }
export interface Step { title: string; desc: string; }
export interface Row { label: string; text: string; }
export interface Swatch { name: string; hex: string; role: string; light?: boolean; }

export interface Card {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  target?: PageId;
}

export interface Side { eyebrow: string; name: string; items: string[]; }
export interface Pair {
  title: string;
  left: Side;
  right: Side;
  target: PageId;
  ctaLabel: string;
}

interface Head { label: string; tone: Tone; title: string; italic: string; intro?: string; }

export type Section =
  | (Head & { type: "overview"; paragraphs: string[]; points: Point[]; image: ImgSlot; principle?: string; tallImage?: boolean })
  | (Head & { type: "steps"; steps: Step[] })
  | (Head & { type: "cards"; variant: "feature" | "decision" | "problem" | "takeaway"; columns: 2 | 3; cards: Card[] })
  | (Head & { type: "gallery"; columns: 1 | 2 | 3; images: ImgSlot[] })
  | (Head & { type: "connected"; pairs: Pair[] })
  | (Head & { type: "rows"; rows: Row[] })
  | (Head & { type: "palette"; swatches: Swatch[]; rules: string[]; images: ImgSlot[] });

export interface PageData {
  id: PageId;
  path: string;
  shortName: string;
  docTitle: string;
  theme: Theme;
  badge: string;
  name: string;
  italic: string;
  tagline: string;
  tags: string[];
  meta: { label: string; val: string }[];
  heroImage: ImgSlot;
  cta?: { label: string; href: string };
  band: { eyebrow: string; text: string; stats: { label: string; val: string }[]; link?: { label: string; to: string } };
  sections: Section[];
  closing: { title: string; italic: string; text: string };
  next: PageId;
  footerNote: string;
  // used by the "Petling ecosystem" cards on the other pages
  eco: { name: string; kind: string; blurb: string; status: string };
}

export const PAGE_ORDER: PageId[] = ["app", "admin", "providers", "website"];

export const PAWPAL_PATH = "/pawpal";

// ────────────────────────────────────────────────────────────────────────────
// 1. PETLING MOBILE APP
// ────────────────────────────────────────────────────────────────────────────
const app: PageData = {
  id: "app",
  path: "/petling-app",
  shortName: "App",
  docTitle: "Petling Mobile App Case Study — Lobna AbouHussein",
  theme: {
    accent: "#3a7d6a", accentOnDark: "#7dd4c0", pale: "#e0f5f0", deep: "#1a3d30",
    heroBg: "#f4f0e8", heroDark: false, glow: "#f4a07a",
  },
  badge: "Case Study · Live Product · iOS & Android",
  name: "Petling",
  italic: "Mobile App.",
  tagline:
    "A health and care companion for pet parents. It began as a Figma concept called PawPal. I took it out of Figma, built it myself, and shipped it to the App Store and Google Play.",
  tags: ["Product Design", "UI/UX", "AI-Assisted Development", "React Native", "Shipped Product"],
  meta: [
    { label: "Role", val: "Founder & Product Designer" },
    { label: "Timeline", val: "June 2026 – Present" },
    { label: "Platform", val: "iOS & Android · Live" },
    { label: "Deliverables", val: "Design, Build, Launch" },
  ],
  heroImage: { id: "app-hero", caption: "The Petling mobile app", ratio: "4 / 5" },
  cta: { label: "Try Petling", href: "https://petling.app/get.html" },
  band: {
    eyebrow: "From concept to live",
    text: "Petling started as PawPal: a concept made only of Figma screens. This is what happened when I decided to build it for real.",
    stats: [
      { label: "Solo-built", val: "Design + Dev" },
      { label: "Live on", val: "iOS & Android" },
      { label: "Part of", val: "3 connected products" },
    ],
    link: { label: "Read the PawPal concept", to: "/pawpal" },
  },
  sections: [
    {
      type: "overview", tone: "light", label: "Overview",
      title: "From Figma screens to a", italic: "live product.",
      paragraphs: [
        "Petling began life as PawPal, a concept I designed entirely in Figma. Screens only: no code, no users, no store listing. You can read that first chapter in the [[/pawpal|PawPal case study]].",
        "This is the next chapter. I decided the design deserved to be tested against reality, so I built it: chose the stack, rebuilt every flow in code, and shipped it on the App Store and Google Play.",
        "The problem is simple. A pet's health is scattered: vaccination cards in a drawer, vet PDFs in an inbox, medication schedules in a notes app, and everyone who helps holds a different piece. Petling puts the whole picture in one place and lets the care team share it.",
      ],
      points: [
        { icon: "Layers", title: "Concept to product", desc: "PawPal was the Figma concept. Petling is what shipped." },
        { icon: "HeartPulse", title: "One place for everything", desc: "Health, documents, reminders and memories in one calm app." },
        { icon: "Users", title: "Shared care", desc: "Co-parents and caregivers join by invite, with their own permissions." },
      ],
      image: { id: "app-overview", caption: "Petling on iOS and Android", ratio: "4 / 3" },
      principle: "Vet visits, vaccinations, prescriptions, and medical documents — organized, reminded, and never lost in your camera roll again.",
    },
    {
      type: "steps", tone: "white", label: "Concept to Launch",
      title: "How Petling became a", italic: "live product.",
      intro: "From a Figma concept to two app stores, in five moves. Every one of them was specified, built and tested by me.",
      steps: [
        { title: "The concept", desc: "[[/pawpal|PawPal]]: research, personas, flows and a full UI kit, designed end to end in Figma." },
        { title: "Choosing the stack", desc: "React Native with Expo and Firebase instead of a no-code builder like FlutterFlow: one codebase for iOS and Android, with a real backend behind it." },
        { title: "Building sprint by sprint", desc: "Notifications and reminders first, then health tracking, documents, care team, memories and reports. Each feature was specified, built and tested before the next." },
        { title: "Testing with real people", desc: "Simulator first, then TestFlight and real devices, with a group of beta testers alongside me, and a closed testing group on Google Play." },
        { title: "Shipping", desc: "App Store review and Google Play production access, then live on both stores in July 2026." },
      ],
    },
    {
      type: "cards", tone: "light", variant: "feature", columns: 3, label: "Features",
      title: "What's inside", italic: "the app.",
      intro: "Everything a pet parent needs to keep track of, and everyone who helps them.",
      cards: [
        { icon: "PawPrint", title: "Multi-pet profiles", body: "Switch between pets in one tap. Each has its own health history, activity log and care team." },
        { icon: "HeartPulse", title: "Health tracking", body: "Weight log, vet visit history, vaccinations, and medications with a custom frequency builder for irregular schedules." },
        { icon: "FileText", title: "Health records", body: "Upload documents into clear categories: Medical, Prescriptions, Admin & Legal, and Other." },
        { icon: "ClipboardList", title: "Health reports", body: "Generate a PDF summary of a pet's health and share it straight from the app, for example with a new vet." },
        { icon: "Bell", title: "Reminders", body: "Push reminders, including ones created automatically from medications and vaccinations." },
        { icon: "Users", title: "Care team", body: "Invite a co-parent or caregiver by link or code and choose what each person can see and do. Invite links open straight in the app." },
        { icon: "Images", title: "Memories", body: "Photos and videos in a swipeable carousel with a full-screen viewer, and permissions for who can view or add." },
        { icon: "CalendarCheck", title: "Care Sessions", body: "When a pet stays at daycare or boarding, the owner follows the stay live. The staff enter it from Petling for Providers.", tag: "Connected", target: "providers" },
        { icon: "ToggleRight", title: "Feature tiers", body: "What each tier can use is controlled remotely from Petling Admin, with no app update needed.", tag: "Connected", target: "admin" },
      ],
    },
    {
      type: "gallery", tone: "white", columns: 3, label: "Screens",
      title: "The app,", italic: "in use.",
      intro: "Screens from the live build.",
      images: [
        { id: "app-gallery-1", caption: "Home", ratio: "4 / 5" },
        { id: "app-gallery-2", caption: "Medical documents organized", ratio: "4 / 5" },
        { id: "app-gallery-3", caption: "Care Team", ratio: "4 / 5" },
        { id: "app-gallery-4", caption: "Smart Reminders", ratio: "4 / 5" },
        { id: "app-gallery-5", caption: "Weight Logs", ratio: "4 / 5" },
        { id: "app-gallery-6", caption: "Care Sessions", ratio: "4 / 5" },
      ],
    },
    {
      type: "connected", tone: "dark", label: "Connected Products",
      title: "Where the app meets", italic: "the rest of Petling.",
      intro: "Petling is three apps on one shared data model. These are the two places where the mobile app is directly driven by another one.",
      pairs: [
        {
          title: "Care Sessions",
          left: {
            eyebrow: "What the pet parent sees", name: "Petling mobile app",
            items: [
              "A Care Sessions list with every stay, past and present",
              "A live timeline of mood, meals, potty breaks and medication",
              "Photos and videos shared during the stay",
              "A New Request button with eight categories, including extend stay",
              "Medical records labeled with the daycare's name in the Health tab",
            ],
          },
          right: {
            eyebrow: "What staff enter", name: "Petling for Providers",
            items: [
              "Check-in by QR code, typed code or phone number",
              "One-tap updates for mood, meals, potty and medication",
              "Photo and video uploads on the pet's page",
              "A requests inbox that answers straight back into the app",
              "A Medical tab that files records into the owner's Health tab",
            ],
          },
          target: "providers", ctaLabel: "See the provider desk",
        },
        {
          title: "Feature tiers",
          left: {
            eyebrow: "What the pet parent experiences", name: "Petling mobile app",
            items: [
              "Features such as Memories switch on and off remotely",
              "Limits apply per tier, free or Pro",
              "No update or store review needed for a change",
            ],
          },
          right: {
            eyebrow: "What I control", name: "Petling Admin",
            items: [
              "A feature flag for every gated feature",
              "Toggle it, or give it a limit, per tier",
              "Users and providers managed alongside",
            ],
          },
          target: "admin", ctaLabel: "See the admin console",
        },
      ],
    },
    {
      type: "cards", tone: "white", variant: "decision", columns: 3, label: "How I Built It",
      title: "Vibe coding,", italic: "with a designer's eye.",
      intro: "I approached the build as product owner and designer, with AI writing the code. Every screen was specified by me, run on a device, and sent back until it was right.",
      cards: [
        { title: "What I owned", body: "Product decisions, flows, screen-by-screen specs, and the final say on how everything looks and behaves." },
        { title: "What AI wrote", body: "The code, generated from my detailed specs, then reviewed, run and corrected in a constant loop." },
        { title: "How I tested", body: "Simulator first, then TestFlight and real devices, with a group of beta testers and me putting every build through its paces." },
      ],
    },
    {
      type: "rows", tone: "light", label: "Sprints",
      title: "Built in", italic: "sprints.",
      intro: "The build, in the order it happened.",
      rows: [
        { label: "Sprint 1", text: "Push notifications, reminders and the animated logo splash." },
        { label: "Sprint 2", text: "Full health tracking: weight log, vet history, vaccinations, medications and health records." },
        { label: "Sprint 3", text: "Document storage with a category and type system." },
        { label: "Care team", text: "Co-parenting and guest access with invite links, permissions and full QA of the guest flow." },
        { label: "Memories", text: "Photos and videos with background uploads, a swipeable carousel and permission controls." },
        { label: "Reports and security", text: "PDF health reports, a full lockdown of data access rules, and verified phone numbers." },
        { label: "Polish pass", text: "Haptic feedback, friendly error messages and consistent text formatting across every form." },
        { label: "Care Sessions", text: "The owner side of the provider platform: check-in, timeline, media and requests." },
      ],
    },
  ],
  closing: {
    title: "See it live,", italic: "not just in Figma.",
    text: "Download the app on iOS or Android, then follow the story into the console and the provider desk behind it.",
  },
  next: "admin",
  footerNote: "Petling Mobile App · Case Study · 2026",
  eco: {
    name: "Petling Mobile App", kind: "Mobile · iOS & Android",
    blurb: "The pet parent's app: health, records, reminders, care team and Care Sessions.",
    status: "Live",
  },
};

// ────────────────────────────────────────────────────────────────────────────
// 2. PETLING ADMIN
// ────────────────────────────────────────────────────────────────────────────
const admin: PageData = {
  id: "admin",
  path: "/petling-admin",
  shortName: "Admin",
  docTitle: "Petling Admin Case Study — Lobna AbouHussein",
  theme: {
    accent: "#3a7d6a", accentOnDark: "#7dd4c0", pale: "#e0f5f0", deep: "#1a3d30",
    heroBg: "#ffffff", heroDark: false, glow: "#7dd4c0",
  },
  badge: "Case Study · Internal Tool · Web",
  name: "Petling",
  italic: "Admin.",
  tagline:
    "The control room I built to run Petling: a private console to see users, switch features on and off per tier, shape the monetization, and onboard the daycare and boarding businesses who partner with Petling.",
  tags: ["Admin Design", "Dashboard", "Feature Flags", "Data Tables", "Internal Tools"],
  meta: [
    { label: "Role", val: "Founder & Product Designer" },
    { label: "Timeline", val: "2026 – Present" },
    { label: "Platform", val: "Web · Private Console" },
    { label: "Deliverables", val: "Design, Build, Operate" },
  ],
  heroImage: { id: "admin-hero", caption: "Petling Admin dashboard", ratio: "16 / 10" },
  band: {
    eyebrow: "Built for one person",
    text: "Petling Admin has exactly one user: me. Every decision favors speed, clarity and safety over collaboration features.",
    stats: [
      { label: "Admins", val: "1, by design" },
      { label: "Controls", val: "Users · Features · Providers" },
      { label: "Powers", val: "App + Provider desk" },
    ],
  },
  sections: [
    {
      type: "overview", tone: "light", label: "Overview",
      title: "The product,", italic: "under control.",
      paragraphs: [
        "Running a live app means constant questions. Who signed up? Who is actually using it? Can I switch this feature off for free users? Which daycare should get an account? Petling Admin answers all of them from one place.",
        "It is the least glamorous part of the ecosystem and one of the most important. Every feature in the [[/petling-app|mobile app]] and every partner on the [[/petling-providers|provider desk]] is governed from here.",
      ],
      points: [
        { icon: "Lock", title: "Private by design", desc: "One admin, one login, no roles to manage." },
        { icon: "SlidersHorizontal", title: "No update needed", desc: "Feature changes reach the app without a release." },
        { icon: "Route", title: "Everything connected", desc: "Users, features and partners live in one shared system." },
      ],
      image: { id: "admin-overview", caption: "Petling Admin", ratio: "4 / 3" },
      principle: "Admin tools deserve real design. When you are the only user, every extra click is one you pay for every single day.",
    },
    {
      type: "cards", tone: "white", variant: "feature", columns: 3, label: "What It Does",
      title: "Six jobs,", italic: "one console.",
      cards: [
        { icon: "LayoutDashboard", title: "Dashboard", body: "The first screen answers the daily question: how is Petling doing?" },
        { icon: "Users", title: "Users", body: "Browse every account, moderate when needed, and delete an account completely, data and sign-in, when someone asks." },
        { icon: "ToggleRight", title: "Feature flags", body: "Switch any feature on or off, or give it a limit, separately for free and Pro tiers. The mobile app reads them live." },
        { icon: "SlidersHorizontal", title: "Monetization controls", body: "The free and Pro experience is defined here: which features each tier gets and where the limits sit." },
        { icon: "Building2", title: "Providers", body: "Add a daycare or boarding business that wants to work with Petling. Required business details keep every partner record complete." },
        { icon: "UserCog", title: "Provider staff", body: "Create logins for a business's team with admin or staff roles, and remove them properly when they leave." },
      ],
    },
    {
      type: "connected", tone: "dark", label: "Connected Products",
      title: "Where these controls", italic: "show up.",
      intro: "The console has no audience of its own. Everything I do here changes what someone sees in another Petling product.",
      pairs: [
        {
          title: "Feature flags and tiers",
          left: {
            eyebrow: "What I control", name: "Petling Admin",
            items: [
              "A feature flag for every gated feature",
              "Toggle it, or set a limit, per tier: free or Pro",
              "Changes apply without an app update",
            ],
          },
          right: {
            eyebrow: "What the pet parent sees", name: "Petling mobile app",
            items: [
              "Features such as Memories appear or disappear remotely",
              "Free and Pro limits are enforced inside the app",
              "No store review needed for a change",
            ],
          },
          target: "app", ctaLabel: "See the mobile app",
        },
        {
          title: "Providers and staff",
          left: {
            eyebrow: "What I do here", name: "Petling Admin",
            items: [
              "Add a business with its required details",
              "Create staff logins with an admin or staff role",
              "Remove staff accounts completely when they leave",
            ],
          },
          right: {
            eyebrow: "What the business gets", name: "Petling for Providers",
            items: [
              "A login for every member of the team",
              "A roster of today's guests, ready on day one",
              "A business profile the business can edit itself",
            ],
          },
          target: "providers", ctaLabel: "See the provider desk",
        },
      ],
    },
    {
      type: "cards", tone: "light", variant: "decision", columns: 3, label: "Design Decisions",
      title: "Decisions that", italic: "shaped it.",
      cards: [
        { title: "Three separate worlds", body: "Admin, provider staff and pet parents each sign in separately, with their own permissions. A staff member can use a different email from their personal Petling account." },
        { title: "Deleting means deleting", body: "Removing a user removes their data and their sign-in account, not just the visible record. Real deletion is a trust decision, and the console makes it explicit." },
        { title: "Power needs guardrails", body: "Destructive actions, single or bulk, always ask for confirmation before anything is removed." },
        { title: "Complete records only", body: "Provider name, phone, email and full address are required, so no partner is ever half-created." },
        { title: "Switches over releases", body: "Turning a feature on, off or limiting it is a setting, not a release. Product decisions stay reversible." },
        { title: "Dense over decorative", body: "Built for one operator: scannable tables, clear status and as few clicks as possible." },
      ],
    },
  ],
  closing: {
    title: "A product is more than", italic: "its screens.",
    text: "The console is private, but the story doesn't stop here. See the app it controls and the provider desk it feeds.",
  },
  next: "providers",
  footerNote: "Petling Admin · Case Study · 2026",
  eco: {
    name: "Petling Admin", kind: "Web · Console",
    blurb: "The control room: users, feature flags, monetization and provider accounts.",
    status: "Private",
  },
};

// ────────────────────────────────────────────────────────────────────────────
// 3. PETLING FOR PROVIDERS
// ────────────────────────────────────────────────────────────────────────────
const providers: PageData = {
  id: "providers",
  path: "/petling-providers",
  shortName: "Providers",
  docTitle: "Petling for Providers Case Study — Lobna AbouHussein",
  theme: {
    accent: "#3a7d6a", accentOnDark: "#7dd4c0", pale: "#e0f5f0", deep: "#1a3d30",
    heroBg: "#e6f2ed", heroDark: false, glow: "#7dd4c0",
  },
  badge: "Case Study · B2B Platform · Web",
  name: "Petling",
  italic: "for Providers.",
  tagline:
    "A web desk for daycare and boarding teams to check pets in, log the day and keep pet parents in the loop. It is the staff side of Petling's Care Sessions.",
  tags: ["B2B Design", "Web App", "Workflow Design", "Responsive", "Pilot"],
  meta: [
    { label: "Role", val: "Founder & Product Designer" },
    { label: "Timeline", val: "2026 – Present" },
    { label: "Platform", val: "Web · Responsive" },
    { label: "Deliverables", val: "Design, Build, Pilot" },
  ],
  heroImage: { id: "providers-hero", caption: "Petling for Providers: today's guests", ratio: "16 / 10" },
  band: {
    eyebrow: "A second product, from real usage",
    text: "A shipped app surfaced a new question: what happens when the pet is not with its owner? This is the answer, built for the person behind the reception desk.",
    stats: [
      { label: "Onboarding", val: "Personal, in pilot" },
      { label: "Check-in", val: "QR · Code · Phone" },
      { label: "Built for", val: "Desks and phones" },
    ],
  },
  sections: [
    {
      type: "overview", tone: "light", label: "Overview",
      title: "Peace of mind,", italic: "logged.",
      paragraphs: [
        "Leaving a pet at daycare or boarding is stressful. Owners want updates. Staff are busy and can't text fifty people.",
        "Petling for Providers is the staff side of Care Sessions. Staff log a pet's day in seconds (mood, meals, potty, medication, photos) and it appears as a live timeline in the [[/petling-app|Petling app]].",
        "Providers are onboarded personally during the pilot, and their accounts are created from [[/petling-admin|Petling Admin]].",
      ],
      points: [
        { icon: "Activity", title: "Fast to log", desc: "Every update is a few taps, designed for busy hands." },
        { icon: "Inbox", title: "Structured, not chat", desc: "Owner requests arrive in categories staff can answer in seconds." },
        { icon: "ShieldCheck", title: "A trustworthy record", desc: "Updates are never quietly rewritten." },
      ],
      image: { id: "providers-overview", caption: "A pet's page and timeline", ratio: "4 / 3" },
      tallImage: true,
      principle: "The best tool for a busy team is the one that asks for the least. Every screen here exists to remove a text message.",
    },
    {
      type: "steps", tone: "white", label: "A Day at the Desk",
      title: "One stay,", italic: "five moments.",
      intro: "The product follows the rhythm of a real stay.",
      steps: [
        { title: "Check in", desc: "Scan a QR code, type a plain readable code, or look the owner up by phone number." },
        { title: "Log the day", desc: "Mood, meals, potty, medication and notes, each in a few taps." },
        { title: "Answer requests", desc: "Structured requests from pet parents land in one inbox." },
        { title: "Share moments", desc: "Photos, videos and medical records go straight to the owner's app." },
        { title: "Check out", desc: "One action closes the stay, cancels leftover requests and turns the page into a read-only summary." },
      ],
    },
    {
      type: "cards", tone: "light", variant: "feature", columns: 3, label: "Features",
      title: "Built for", italic: "the front desk.",
      cards: [
        { icon: "QrCode", title: "Check-in", body: "QR code, a plain readable code the owner types into the app, or the owner's phone number." },
        { icon: "ClipboardList", title: "Today's guests", body: "A roster of pets currently present, with a Present and Past toggle. Past stays are grouped by pet, one card each." },
        { icon: "PawPrint", title: "Pet page", body: "Tabs for logging updates, the Timeline, Photos and videos, Requests and Medical. Check out or extend a stay from the same page." },
        { icon: "Activity", title: "Log an update", body: "Mood, meals, potty, medication and notes, saved as an append-only record. Mistakes are fixed with a new entry." },
        { icon: "Inbox", title: "Requests inbox", body: "Extend stay, photo, video, mood, food, bathroom and health requests, with a live count badge, status pills and an All, Pending, Resolved filter." },
        { icon: "Stethoscope", title: "Medical records", body: "Staff upload a record during a stay. It appears in the owner's Health tab labeled with the business name." },
        { icon: "History", title: "Pet history", body: "Every past stay stacked on a single page, so a pet's whole history reads at a glance." },
        { icon: "Building2", title: "Business profile", body: "The provider's own details, editable by the business, with required name, phone, email and address." },
        { icon: "Smartphone", title: "Owners not on Petling yet", body: "Staff can check a pet in by phone number before the owner has an account. The pet is held for them and handed over when they sign up.", tag: "In progress" },
      ],
    },
    {
      type: "gallery", tone: "white", columns: 1, label: "Screens",
      title: "The desk,", italic: "in use.",
      images: [
        { id: "providers-detail", caption: "Provider details", ratio: "16 / 10" },
      ],
    },
    {
      type: "connected", tone: "dark", label: "Connected Products",
      title: "What staff enter,", italic: "what owners see.",
      intro: "Every action on this desk shows up somewhere else in Petling. These are the three places it lands.",
      pairs: [
        {
          title: "Updates become a timeline",
          left: {
            eyebrow: "What staff enter", name: "Petling for Providers",
            items: [
              "Mood, meals, potty and medication logged in a few taps",
              "Photos and videos uploaded on the pet's page",
              "Medical records uploaded on the Medical tab",
            ],
          },
          right: {
            eyebrow: "What the pet parent sees", name: "Petling mobile app",
            items: [
              "A live timeline inside Care Sessions",
              "Photos and videos in the Media tab",
              "Medical records filed into the Health tab",
            ],
          },
          target: "app", ctaLabel: "See Care Sessions in the app",
        },
        {
          title: "Requests flow back",
          left: {
            eyebrow: "What the pet parent sends", name: "Petling mobile app",
            items: [
              "A New Request with eight categories",
              "Extend-stay requests carry a new end date",
              "A status they can follow: pending, answered, cancelled",
            ],
          },
          right: {
            eyebrow: "What staff receive", name: "Petling for Providers",
            items: [
              "One inbox with a live count badge",
              "Shortcut buttons that jump to the right tab",
              "Requests that resolve themselves once the work is done",
            ],
          },
          target: "app", ctaLabel: "See requests in the app",
        },
        {
          title: "Accounts start in Admin",
          left: {
            eyebrow: "What I do", name: "Petling Admin",
            items: [
              "Add the business with its required details",
              "Create a login for each member of staff",
            ],
          },
          right: {
            eyebrow: "What the business gets", name: "Petling for Providers",
            items: [
              "A team login on day one",
              "A roster and business profile, ready to use",
            ],
          },
          target: "admin", ctaLabel: "See the admin console",
        },
      ],
    },
  ],
  closing: {
    title: "Built for the people", italic: "behind the desk.",
    text: "Follow a stay from both sides: what staff enter here, and what the pet parent sees in the app.",
  },
  next: "website",
  footerNote: "Petling for Providers · Case Study · 2026",
  eco: {
    name: "Petling for Providers", kind: "Web · B2B",
    blurb: "The desk for daycare and boarding teams: check-in, daily logs and requests.",
    status: "Pilot",
  },
};

// ────────────────────────────────────────────────────────────────────────────
// 4. PETLING WEBSITE & BRAND
// ────────────────────────────────────────────────────────────────────────────
const website: PageData = {
  id: "website",
  path: "/petling-website",
  shortName: "Website",
  docTitle: "Petling Website & Brand Case Study — Lobna AbouHussein",
  theme: {
    accent: "#3a7d6a", accentOnDark: "#7dd4c0", pale: "#e0f5f0", deep: "#1a3d30",
    heroBg: "#f4f0e8", heroDark: false, glow: "#7dd4c0",
  },
  badge: "Case Study · Brand & Web · petling.app",
  name: "Petling",
  italic: "Website & Brand.",
  tagline:
    "The commercial website and brand system that introduce Petling: a home for the story, a smart path to the right app store, and the visual identity that ties the whole ecosystem together.",
  tags: ["Web Design", "Brand Identity", "Motion", "Social Templates", "Launch Assets"],
  meta: [
    { label: "Role", val: "Brand & Web Designer" },
    { label: "Timeline", val: "2026" },
    { label: "Platform", val: "Web · petling.app" },
    { label: "Deliverables", val: "Site, Brand, Launch Assets" },
  ],
  heroImage: { id: "website-hero", caption: "petling.app", ratio: "16 / 10" },
  cta: { label: "Visit petling.app", href: "https://petling.app" },
  band: {
    eyebrow: "One brand, many surfaces",
    text: "The same calm palette and tone runs through the website, the app, the console and the provider desk.",
    stats: [
      { label: "Live at", val: "petling.app" },
      { label: "Palette", val: "3 core colors" },
      { label: "Surfaces", val: "Site · App · Console · Desk" },
    ],
  },
  sections: [
    {
      type: "overview", tone: "light", label: "Overview",
      title: "The public face of", italic: "Petling.",
      paragraphs: [
        "petling.app is where a pet parent first meets Petling. It has three jobs: tell the story, get every visitor to the right app store, and support the [[/petling-app|apps]] once they are installed.",
        "It also had to feel like the product. Same calm palette, same warmth, same lack of noise, so the step from website to app feels like walking through one door.",
      ],
      points: [
        { icon: "Sparkles", title: "Story", desc: "Why Petling exists, in a few calm screens." },
        { icon: "Download", title: "Download", desc: "One tap to the right store for the right phone." },
        { icon: "ShieldCheck", title: "Support", desc: "Help and legal pages in their own place." },
      ],
      image: { id: "website-overview", caption: "petling.app on desktop and mobile", ratio: "4 / 3" },
    },
    {
      type: "cards", tone: "white", variant: "feature", columns: 3, label: "What I Designed",
      title: "More than a", italic: "homepage.",
      cards: [
        { icon: "Globe", title: "Marketing site", body: "The home page and the story behind the app, with the same warmth as the product." },
        { icon: "Smartphone", title: "Smart download page", body: "Sends iOS and Android visitors to the right store automatically, so no one has to choose." },
        { icon: "Link2", title: "Invite landing", body: "Shared care-team invite links open directly in the app if it is installed, and in the store if not.", tag: "Connected", target: "app" },
        { icon: "ShieldCheck", title: "Support and legal", body: "Support and legal pages, with legal on its own subdomain." },
        { icon: "Megaphone", title: "Launch campaign", body: "An Instagram campaign aimed at Egypt, the UAE and Saudi Arabia, with a redirect page that routes each phone to its store." },
        { icon: "Store", title: "Store presence", body: "App Store and Google Play listing copy, keywords and screenshots, written and designed to match the brand." },
      ],
    },
    {
      type: "gallery", tone: "light", columns: 3, label: "The Site",
      title: "The website,", italic: "in use.",
      images: [
        { id: "website-features", caption: "Features", ratio: "4 / 3" },
        { id: "website-support", caption: "Support", ratio: "4 / 3" },
        { id: "website-story", caption: "Our Story", ratio: "4 / 3" },
      ],
    },
    {
      type: "palette", tone: "dark", label: "Brand System",
      title: "A calm palette,", italic: "used sparingly.",
      intro: "Three core colors, on white. The restraint is the brand.",
      swatches: [
        { name: "Deep Teal", hex: "#2D3E3E", role: "Primary" },
        { name: "Soft Teal", hex: "#EAEFEF", role: "Surface", light: true },
        { name: "Mint", hex: "#5DCAA5", role: "Accent" },
      ],
      rules: [
        "Teal on white only. No decorative gradients or extra hues.",
        "Small functional accents appear only in category tags and pills.",
        "The logo is a paw and heart mark, with an animated version that draws itself in.",
        "Figma holds the master templates. Canva is used to produce them at scale.",
      ],
      images: [
        { id: "website-logo", caption: "Logo", ratio: "4 / 3" },
      ],
    },
    {
      type: "cards", tone: "white", variant: "feature", columns: 3, label: "Brand in Use",
      title: "Assets for", italic: "launch.",
      cards: [
        { icon: "Shapes", title: "Logo and motion", body: "The paw and heart mark, plus an animated version that draws itself in over two seconds." },
        { icon: "Palette", title: "Social templates", body: "Master templates in Figma, then editable Canva designs that follow the brand kit, for announcements and quotes." },
        { icon: "Clapperboard", title: "Launch video concept", body: "A stop-motion, paper-craft video on a pale sage background, with four animal characters." },
      ],
    },
  ],
  closing: {
    title: "Every product needs", italic: "a front door.",
    text: "Visit the live site, or go back to where it starts: the app in your pocket.",
  },
  next: "app",
  footerNote: "Petling Website & Brand · Case Study · 2026",
  eco: {
    name: "Petling Website & Brand", kind: "Web · Brand",
    blurb: "petling.app and the brand system: download paths, invite links and launch assets.",
    status: "Live",
  },
};

export const PAGES: Record<PageId, PageData> = { app, admin, providers, website };

export function getPetlingPageByPath(pathname: string): PageData | null {
  const clean = pathname.length > 1 && pathname.charAt(pathname.length - 1) === "/"
    ? pathname.slice(0, -1)
    : pathname;
  for (let i = 0; i < PAGE_ORDER.length; i++) {
    const p = PAGES[PAGE_ORDER[i]];
    if (p.path === clean) return p;
  }
  return null;
}
